import supabaseClient from '@/api/supabaseClient.ts';
import { SupabaseClient } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { Faq } from '@/types/dto/Faq';

type Filters =
  | {
      restaurant_id?: number | null;
      question_id?: number | null;
    }
  | undefined;
const useFaq = (enabled: boolean = true, filters: Filters = undefined) => {
  const client = supabaseClient;
  const queryKey = ['faq', filters?.restaurant_id, filters?.question_id];

  const queryFn = async () => {
    return getFaq(client, filters);
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export default useFaq;

async function getFaq(client: SupabaseClient, filters: Filters): Promise<Faq[]> {
  let clientWithFilters = client
    .from('questions')
    .select(
      `
        id,
        restaurant_id (id, name),
        question,
        created_at
      `
    )
    .order('created_at', { ascending: false });

  if (filters?.restaurant_id) {
    clientWithFilters = clientWithFilters.eq('restaurant_id', filters.restaurant_id);
  }

  if (filters?.question_id) {
    clientWithFilters = clientWithFilters.eq('id', filters.question_id);
  }

  const { data: questions, error: errorQuestions } = await clientWithFilters;

  if (errorQuestions) throw new Error(errorQuestions.message);

  if (!questions) return [];

  const { data: answers, error: errorAnswers } = await client
    .from('answers')
    .select(
      `
      id,
      question_id,
      answer
    `
    )
    .in(
      'question_id',
      questions.map((question) => question.id)
    );

  if (errorAnswers) throw new Error(errorAnswers.message);

  const response: Faq[] = (
    questions as unknown as {
      id: number;
      restaurant_id: { id: number; name: string };
      question: string;
      created_at: string;
    }[]
  ).map((question) => {
    const answerForQuestion = answers?.find((answer) => answer.question_id === question.id);
    return {
      question: question.question,
      question_id: question.id,
      answer: answerForQuestion?.answer ?? null,
      answer_id: answerForQuestion?.id ?? null,
      restaurant: question.restaurant_id.name,
      restaurant_id: question.restaurant_id.id,
      created_at: question.created_at,
    };
  });

  return response;
}
